import { log2p1 } from './transforms';

// RdBu_r colorscale for z-score
export const RDBU_R = [
  [0, '#2166ac'], [0.25, '#67a9cf'], [0.5, '#f7f7f7'],
  [0.75, '#ef8a62'], [1, '#b2182b'],
];

// YlOrRd for absolute
export const YLORRD = [
  [0, '#ffffcc'], [0.25, '#fed976'], [0.5, '#fd8d3c'],
  [0.75, '#e31a1c'], [1, '#800026'],
];

/**
 * Compute dot plot data from a gene list and expression data.
 *
 * @param {Object} params
 * @param {string[]} params.genes - ordered gene list
 * @param {Object} params.expression - gene -> [N TPM values]
 * @param {string[]} params.colLabels - N column labels
 * @param {string} params.colorMode - 'relative' or 'absolute'
 * @param {Object} [params.subcatMap] - gene -> subcategory name (optional)
 * @returns {Object} plotData
 */
export function computeDotPlotData({ genes, expression, colLabels, colorMode, subcatMap }) {
  const nCols = colLabels.length;
  const matrix = [];
  const missingRows = [];
  const subcats = [];

  for (let i = 0; i < genes.length; i++) {
    const gene = genes[i];
    if (subcatMap) {
      subcats.push(subcatMap[gene] || '');
    }
    const expr = expression[gene];
    if (expr) {
      matrix.push(expr);
    } else {
      matrix.push(null);
      missingRows.push(i);
    }
  }

  const logMatrix = matrix.map(row =>
    row ? row.map(log2p1) : Array(nCols).fill(0)
  );

  let colorValues, colorscale, cmin, cmax;

  if (colorMode === 'relative') {
    colorValues = logMatrix.map((row, i) => {
      if (missingRows.includes(i)) return Array(nCols).fill(0);
      const mean = row.reduce((a, b) => a + b, 0) / row.length;
      const std = Math.sqrt(row.reduce((a, b) => a + (b - mean) ** 2, 0) / row.length) || 1;
      return row.map(v => Math.max(-3, Math.min(3, (v - mean) / std)));
    });
    colorscale = RDBU_R;
    cmin = -3;
    cmax = 3;
  } else {
    colorValues = logMatrix;
    colorscale = YLORRD;
    cmin = 0;
    cmax = Math.max(...logMatrix.flat().filter(v => v > 0), 1);
  }

  const maxLog = Math.max(...logMatrix.flat().filter((_, idx) => {
    const row = Math.floor(idx / nCols);
    return !missingRows.includes(row);
  }), 1);

  const x = [], y = [], sizes = [], colors = [], hoverTexts = [];
  for (let i = 0; i < genes.length; i++) {
    if (missingRows.includes(i)) continue;
    for (let j = 0; j < nCols; j++) {
      const logVal = logMatrix[i][j];
      const rawVal = matrix[i][j];
      x.push(j);
      y.push(i);
      sizes.push(Math.max(4, (logVal / maxLog) * 30));
      colors.push(colorValues[i][j]);
      hoverTexts.push(
        `<b>${genes[i]}</b><br>${colLabels[j]}<br>` +
        `TPM: ${rawVal.toFixed(1)}<br>log₂(TPM+1): ${logVal.toFixed(2)}` +
        (colorMode === 'relative' ? `<br>z-score: ${colorValues[i][j].toFixed(2)}` : '')
      );
    }
  }

  // Size legend reference values
  const sizeRefVals = [2, Math.round(maxLog / 2 * 10) / 10, Math.round(maxLog * 10) / 10]
    .filter((v, i, a) => v > 0 && a.indexOf(v) === i)
    .sort((a, b) => a - b);

  return {
    genes, nCols, x, y, sizes, colors, hoverTexts,
    colorscale, cmin, cmax, missingRows, maxLog, sizeRefVals,
    colorbarTitle: colorMode === 'relative' ? 'Z-score' : 'log₂(expr+1)',
    subcats: subcats.length ? subcats : null,
  };
}
